import {
  hasComparisonSides,
  varianceAnalysisSearchSchema,
  type CostTypeCellScope,
  type CostTypeVarianceScope,
} from '@/hooks/queries/crt/useVarianceAnalysis'
import {
  getValidVarianceFilterGroups,
  NONE_VALUE,
  PRODUCT_CATEGORY,
  VARIANCE_COST_SOURCES,
  VARIANCE_COST_TYPES,
  type VarianceCostSource,
  type VarianceCostType,
  type VarianceDrillFilters,
  type VarianceFilterGroup,
  type VarianceGroupingDimension,
} from '@repo/constants'
import { redirect } from '@tanstack/react-router'
import { z } from 'zod'
import { type DrillDimension, type DrillRow } from './drill-dimensions'
import { DEFAULT_VARIANCE_QUICK_FILTER, resolveVarianceScope } from './resolveVarianceScope'

/**
 * The heat-map cell the chain started from, as one search param per dimension.
 *
 * One param each rather than an encoded blob, so a drill link is readable and can be hand-built — the same
 * reasoning behind the flat `…A` / `…B` side params the comparison already uses.
 *
 * Every one of them carries a **code**, not display text: `cellMarking` a `MarkingCode`,
 * `cellPriceSegment` a SAP segment, `cellBrandCode` a brand-house code. A drill param doubles as a
 * query filter, and only a code is one the material scope can be narrowed by.
 */
const CELL_SEARCH_KEY_BY_DIMENSION = {
  priceSegment: 'cellPriceSegment',
  brand: 'cellBrandCode',
  packContentSize: 'cellPackContentSize',
  productType: 'cellProductType',
  subChannel: 'cellSubChannel',
  marking: 'cellMarking',
} as const

type CellDimension = keyof typeof CELL_SEARCH_KEY_BY_DIMENSION

/** The cell params, in the order the heat map reads them: the row key first, then the column. */
const CELL_SEARCH_KEYS = Object.values(CELL_SEARCH_KEY_BY_DIMENSION)

/**
 * Which search params carry each filter group, in chain order.
 *
 * The one piece of the rule that lives here rather than in `@repo/constants`, because it is a translation
 * and not a decision: the URL says `costType` where the endpoint says `kpiKey`, and a group is a set of
 * params rather than a single one. What each breakdown *accepts* is `getValidVarianceFilterGroups`, read
 * through this map by {@link clearInvalidFilters}.
 *
 * Three dimensions the tables render are deliberately absent. `sapErp` is never a filter — no
 * forward-looking source names an account number, so it could not narrow the comparison's other side —
 * and `programName` and `broaderBucket` are labels for an identity carried elsewhere
 * (`manualInvestmentId`, `budgetAllocation`).
 */
const DRILL_SEARCH_KEYS_BY_FILTER_GROUP = {
  costType: ['costType', 'source'],
  manualInvestment: ['manualInvestmentId'],
  agreement: ['masterAgreement', 'agreementNumber'],
  customer: ['hierarchyErp'],
  budget: ['budgetAllocation'],
} as const satisfies Record<VarianceFilterGroup, readonly DrillDimension[]>

/** Every param the drill chain writes, in chain order. */
const DRILL_SEARCH_KEYS = Object.values(DRILL_SEARCH_KEYS_BY_FILTER_GROUP).flat()

export type DrillSearchKey = (typeof DRILL_SEARCH_KEYS_BY_FILTER_GROUP)[VarianceFilterGroup][number]

/** Anything that can show up as an applied-filter badge, in the order the badges read. */
const DRILL_FILTER_KEYS = [...CELL_SEARCH_KEYS, ...DRILL_SEARCH_KEYS] as const
type DrillFilterKey = (typeof DRILL_FILTER_KEYS)[number]

const optionalString = z.string().optional()

/**
 * The drill pages take the whole parent search plus the cell and the accumulated drill dimensions.
 *
 * Extending the parent's schema rather than restating it is what lets a back link forward the entire
 * search object: the comparison sides, the scope, the selected measure and the currency all survive the
 * round trip untouched.
 */
export const drillSearchSchema = varianceAnalysisSearchSchema.extend({
  cellPriceSegment: optionalString,
  cellBrandCode: optionalString,
  cellPackContentSize: optionalString,
  cellProductType: optionalString,
  cellSubChannel: optionalString,
  cellMarking: optionalString,
  costType: optionalString,
  source: optionalString,
  manualInvestmentId: optionalString,
  masterAgreement: optionalString,
  agreementNumber: optionalString,
  hierarchyErp: optionalString,
  budgetAllocation: optionalString,
})

export type DrillSearch = z.infer<typeof drillSearchSchema>

/**
 * Everything the drill header's filter panels can write.
 *
 * Written out rather than `Partial<DrillSearch>` for the same reason `ComparisonPatch` is: a panel must
 * not be able to reach a param it does not own, and a reviewer should be able to see that from the type.
 * The preset keys are in here because changing the product category has to move the preset with it —
 * `resolveVarianceScope` falls back to the slice for any preset key the URL does not carry, so clearing
 * one alone brings the old category's scope straight back.
 */
export type DrillFiltersPatch = Partial<
  Pick<
    DrillSearch,
    | 'cellPriceSegment'
    | 'cellBrandCode'
    | 'cellPackContentSize'
    | 'cellProductType'
    | 'cellSubChannel'
    | 'cellMarking'
    | 'costType'
    | 'source'
    | 'productCategory'
    | 'quickFilter'
    | 'materialGroupCode'
    | 'packContentSize'
    | 'productTypeLike'
    | 'brandHouseCode'
  >
>

/**
 * Every drill page's `beforeLoad`: without both comparison sides named, there is nothing to drill into, so
 * the reader goes back to the configurator rather than onto a page that can only tell them so.
 *
 * The whole search rides along. The parent route parses it and keeps what it owns — the scope, the quick
 * filter, the currency and whichever sides *were* set — so the reader lands on the configurator with their
 * work in it, and the drill params they cannot use are dropped on the way.
 *
 * Only absence redirects. A cell that names a dimension this page cannot report, or a comparison whose two
 * sides do not line up, is a page the reader can read and correct, and each already says so in place.
 */
export function requireComparisonSides(search: DrillSearch) {
  if (hasComparisonSides(search)) return

  throw redirect({ to: '/crt/variance-analysis', search })
}

/**
 * A dimension whose value is genuinely `null` — a material with no brand house, or none with a marking —
 * is a real reading and has to stay distinguishable from a dimension the row is simply not keyed on.
 * `NONE_VALUE` is what the codebase already uses for exactly that in a URL, so no new sentinel is
 * invented here.
 */
export const encodeDrillValue = (value: string | null | undefined): string | undefined =>
  value === undefined ? undefined : value === null ? NONE_VALUE : value

export const decodeDrillValue = (value: string | undefined): string | null | undefined =>
  value === NONE_VALUE ? null : value

export type DrillFilter = { key: DrillFilterKey; value: string }

/**
 * The filters currently in force, in chain order — the cell that started the drill, then one entry per
 * step taken since. This is what the badges above every drill table report, so a reader can always see
 * what the figures are narrowed to.
 */
export function resolveDrillFilters(search: DrillSearch): DrillFilter[] {
  return DRILL_FILTER_KEYS.flatMap((key) => {
    const value = search[key]
    return value === undefined || value === '' ? [] : [{ key, value }]
  })
}

/** Writes the cell's dimensions, so `/by-cost-type` opens scoped to the cell that was clicked. */
export function buildCellDrillSearch(
  search: DrillSearch,
  cell: Partial<Record<CellDimension, string | null>>,
): DrillSearch {
  const cellParams = Object.fromEntries(
    Object.entries(CELL_SEARCH_KEY_BY_DIMENSION).map(([dimension, key]) => [
      key,
      encodeDrillValue(cell[dimension as CellDimension]),
    ]),
  )

  return { ...search, ...cellParams }
}

/**
 * Writes the dimensions a clicked row is keyed on, so the next step opens narrowed to it — or `null` when
 * the row has no value to be narrowed by.
 *
 * Only the named dimensions are written: a row carries its parent's dimensions too, and re-writing every
 * one of them would let a page silently re-scope a filter it does not own.
 *
 * `null` and `undefined` part company here, which is the distinction `grouping` is typed for. A dimension
 * the row simply is not *keyed* on is `undefined` — a `/by-agreement` parent names a master agreement and
 * no agreement number, and drilling it by customer is a real reading — so the param is left off. A
 * dimension the row **is** keyed on whose value is genuinely absent is `null`: the unattributed master
 * agreement, a plain store with no Commercial-Partner code. Neither answer exists for it. The sentinel
 * would reach the endpoint as a literal and match nothing, and dropping it would answer the unfiltered
 * question under a badge claiming otherwise, so no link is built at all and the row offers no action —
 * the same refusal `drillRoutesFor` makes when the rule grants no breakdown.
 */
export function buildRowDrillSearch(
  search: DrillSearch,
  row: DrillRow,
  dimensions: readonly DrillSearchKey[],
): DrillSearch | null {
  if (dimensions.some((dimension) => row.grouping[dimension] === null)) return null

  const params = Object.fromEntries(
    dimensions.map((dimension) => [dimension, encodeDrillValue(row.grouping[dimension])]),
  )

  return { ...search, ...params }
}

/**
 * Drops every drill param except the ones named, keeping the cell and the comparison intact.
 *
 * This is what a back link needs: returning to an earlier step must not carry the filters the later steps
 * wrote, or the step you land on shows badges for a scope it does not actually have.
 */
export function retainDrillKeys(search: DrillSearch, keys: readonly DrillSearchKey[]): DrillSearch {
  const cleared = Object.fromEntries(
    DRILL_SEARCH_KEYS.filter((key) => !keys.includes(key)).map((key) => [key, undefined]),
  )

  return { ...search, ...cleared }
}

/**
 * The search a back link should carry, and the filters it had to drop to get there.
 *
 * The rule is the shared one: a breakdown accepts the filter groups `getValidVarianceFilterGroups` grants
 * it, and never the group its own table is keyed on — `/by-cost-type` enumerates every cost type and
 * source, so a `costType` badge there would name a scope the table is a total over.
 *
 * The reader may have changed the material, channel or cost-type filters on the page they are leaving, so
 * a back link re-navigates with the search **as it now stands** rather than restoring the entry they
 * arrived from. That is what makes the dropped filters worth naming: {@link retainDrillKeys} silently
 * clears them, and `clearedFilters` is what lets the caller say so.
 *
 * Only keys that actually carried a value are reported, so a toast never names a filter the reader did not
 * have. A `null` target is the heat map, which no drill param describes.
 */
export function clearInvalidFilters(
  search: DrillSearch,
  target: VarianceGroupingDimension | null,
): { newSearch: DrillSearch; clearedFilters: DrillSearchKey[] } {
  const retained: readonly DrillSearchKey[] = target
    ? getValidVarianceFilterGroups(target).flatMap(
        (group) => DRILL_SEARCH_KEYS_BY_FILTER_GROUP[group],
      )
    : []

  return {
    newSearch: retainDrillKeys(search, retained),
    clearedFilters: DRILL_SEARCH_KEYS.filter((key) => !retained.includes(key) && search[key]),
  }
}

/**
 * The value a cell param carries, or `undefined` when there is nothing there to filter on.
 *
 * `NONE_VALUE` is the second of those. A cell keyed on a genuinely absent dimension — a SKU with no
 * brand house, one with no marking — is a real group on the heat map and carries real dollars, but it
 * is not a value the breakdown can be narrowed to, so it reads here as no value at all.
 */
const cellValue = (value: string | undefined): string | undefined =>
  value && value !== NONE_VALUE ? value : undefined

/**
 * The heat-map cell in the search, as the endpoint's scope — or `null` when the cell does not name
 * everything its category needs.
 *
 * This is the only gate: the scope type requires each category's own dimensions, so a scope that
 * comes back at all is one the endpoint will accept, and there is nothing left for the caller to
 * check. The cell params and those dimensions are the same set by construction — both are what the
 * heat map groups that category by, and both are codes — so building one is a rename rather than a
 * translation.
 */
export function toCostTypeScope(search: DrillSearch): CostTypeVarianceScope | null {
  // Resolved through the heat map's own resolver rather than read off the URL: the category can come
  // from the quick-filter preset instead, and a drill that guessed it would report a different
  // category's figures than the cell the reader clicked.
  const { productCategory } = resolveVarianceScope(search, DEFAULT_VARIANCE_QUICK_FILTER)

  const marking = cellValue(search.cellMarking)
  if (!marking) return null

  if (productCategory === PRODUCT_CATEGORY.FMC) {
    const priceSegment = cellValue(search.cellPriceSegment)
    const brandHouseCode = cellValue(search.cellBrandCode)
    const packContentSize = cellValue(search.cellPackContentSize)
    if (!priceSegment || !brandHouseCode || !packContentSize) return null

    return { productCategory, marking, priceSegment, brandHouseCode, packContentSize }
  }

  const productType = cellValue(search.cellProductType)
  const subChannel = cellValue(search.cellSubChannel)
  if (!productType || !subChannel) return null

  return { productCategory, marking, productType, subChannel }
}

/**
 * The heat-map cell **and** the cost-type cell in the search, as a leaf endpoint's scope — or `null`
 * when either is incomplete.
 *
 * Shared by every drill below `/by-cost-type`: they gate on the identical thing, and one resolver is
 * what stops two of them scoping the same cell two different ways.
 *
 * The cost type and source are checked against the vocabularies the page already owns rather than
 * forwarded blind: a search param is anything a URL says it is, and a value outside those lists names a
 * cell that does not exist on the table above. Holding the query back reads as "nothing to show here",
 * which is the truth, where sending it would produce a 400 the reader can do nothing with.
 *
 * The material half is delegated to {@link toCostTypeScope} rather than re-derived, for the same
 * reason — the step above resolves the very same cell.
 */
export function toCostTypeCellScope(search: DrillSearch): CostTypeCellScope | null {
  const cell = toCostTypeScope(search)
  if (!cell) return null

  const { costType: kpiKey, source } = search
  if (!kpiKey || !(VARIANCE_COST_TYPES as readonly string[]).includes(kpiKey)) return null
  if (!source || !(VARIANCE_COST_SOURCES as readonly string[]).includes(source)) return null

  // Forwarded as they stand rather than checked against this page's dimension: which groups an endpoint
  // accepts is its own rule, and a group it cannot take comes back as a 422 naming the breakdowns that
  // could have — which is more than this side could say. `clearInvalidFilters` is what stops a *link*
  // building one in the first place.
  return {
    ...cell,
    kpiKey,
    source,
    manualInvestmentId: search.manualInvestmentId,
    masterAgreement: search.masterAgreement,
    agreementNumber: search.agreementNumber,
    hierarchyErp: search.hierarchyErp,
    budgetAllocation: search.budgetAllocation,
  }
}

/**
 * The search as the drill rule reads it.
 *
 * The rule in `@repo/constants` is stated over the endpoints' own filter names; the URL carries the same
 * set as loose strings. This is the one translation between them, so every page offering a row action
 * asks the rule the same question.
 *
 * `costType` and `source` are narrowed against their value sets rather than cast, the same way
 * {@link toCostTypeCellScope} narrows them: a hand-edited URL can carry anything, and an unrecognised
 * value has to read as "not narrowed by that" rather than as a cost type that does not exist. A
 * `manualInvestmentId` that is not a number reads the same way — the rule takes an id, not a string.
 *
 * FIXME(variance-ar): `measure` is not forwarded, so the rule always evaluates as NTO here. Only a
 * hand-built URL reaches that now — the leaf drills no longer offer the measure chip, and the AR ladder
 * offers no drill links at all because `/by-cost-type` passes the measure at its own call site — but a
 * reader who does land on `/by-agreement` with `measure=ar` in the search is still offered row actions
 * the endpoint would refuse. Forwarding it needs {@link KpiFilterPanel} to opt out first: it spreads
 * this to ask which cost type/source pairs reach its page, a question the measure does not bear on, and
 * would otherwise empty its source list and disable Apply for good.
 */
export function toDrillFilters(search: DrillSearch): VarianceDrillFilters {
  const { costType, source, manualInvestmentId } = search
  const id = manualInvestmentId === undefined ? undefined : Number(manualInvestmentId)

  return {
    kpiKey:
      costType && (VARIANCE_COST_TYPES as readonly string[]).includes(costType)
        ? (costType as VarianceCostType)
        : undefined,
    source:
      source && (VARIANCE_COST_SOURCES as readonly string[]).includes(source)
        ? (source as VarianceCostSource)
        : undefined,
    manualInvestmentId: id !== undefined && Number.isFinite(id) ? id : undefined,
    masterAgreement: search.masterAgreement,
    agreementNumber: search.agreementNumber,
    hierarchyErp: search.hierarchyErp,
    budgetAllocation: search.budgetAllocation,
  }
}
